import React, { FC, Fragment, useState } from 'react';
import classNames from 'classnames';
import { Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import {
  FeedKey,
  UpdateUserProfileOnboardingMutation,
  UpdateUserProfileOnboardingMutationVariables,
  useGetUserClientFollowMappingsQuery,
  useUpdateUserProfileOnboardingMutation,
} from 'api';
import {
  ActionContainer,
  ClientLogo,
  FollowToggle,
  SubmitButton,
  Loaders,
  Layouts,
  IFollowToggleRenderProps,
} from 'components';
import { useProtectedClient, useCurrentUser, useNotify, useUpdateCurrentUser } from 'hooks';
import { index } from 'paths';

import { GroupCard, UserCard } from '../components';

type Onboarding = UpdateUserProfileOnboardingMutation;
type OnboardingVariables = UpdateUserProfileOnboardingMutationVariables;

const REQUIRED_FOLLOW_COUNT = 3;

const useStyles = makeStyles(theme => ({
  chooseButton: {
    width: 250,
    '&:disabled': {
      color: theme.palette.primary.main,
    },
  },
  main: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(10),

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3),
    },
  },
  mainLeft: {
    [theme.breakpoints.up('md')]: {
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(10),
    },
  },
  mainRight: {
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(10),
    },
  },
}));

const Follows: FC = () => {
  const [working, setWorking] = useState(false);
  const [followCount, setFollowCount] = useState(0);
  const { t } = useTranslation();

  const classes = useStyles();
  const currentUser = useCurrentUser();
  const history = useHistory();
  const notify = useNotify();
  const client = useProtectedClient();

  const updateOnboardingOptions = useUpdateCurrentUser<Onboarding, OnboardingVariables>();
  const { mutate } = useUpdateUserProfileOnboardingMutation({
    onSuccess: handleCompleted,
    ...updateOnboardingOptions,
  });

  const { data, isLoading } = useGetUserClientFollowMappingsQuery(null, {
    onError: notify.queryError,
  });

  if (isLoading) {
    return <Loaders.Startup />;
  }

  const { suggested_users, suggested_groups } = data.getUserClientFollowMappings;
  const needsFollows = followCount < REQUIRED_FOLLOW_COUNT;
  const followsNeeded = REQUIRED_FOLLOW_COUNT - followCount;

  function handleCompleted() {
    setWorking(false);
    history.push(index);
  }

  function handleFollowToggle(followToggleProps: IFollowToggleRenderProps) {
    const { isFollowing, onClick } = followToggleProps;

    const updatedFollowCount = isFollowing ? followCount - 1 : followCount + 1;
    setFollowCount(updatedFollowCount);
    void onClick();
  }

  function onFinish() {
    const variables = {
      data: {
        id: currentUser.id,
        last_onboarding_step: 2,
        onboarding_completed: true,
      },
    };

    setWorking(true);

    mutate({ ...variables });
  }

  function renderAction() {
    return needsFollows ? (
      <Button disabled variant="outlined" size="large" className={classes.chooseButton}>
        {t('onboarding:sidebarGroupsButtonCount', { followCount: followsNeeded })}
      </Button>
    ) : (
      <Fragment>
        <SubmitButton
          working={working}
          onClick={onFinish}
          text={t('onboarding:sidebarFollowsFinish')}
        />
      </Fragment>
    );
  }

  function renderGroups() {
    const mainClasses = classNames(classes.main, classes.mainLeft);

    return (
      <div className={mainClasses}>
        <Typography paragraph variant="h4">
          {t('common:group_plural')}
        </Typography>
        {suggested_groups.map(group => (
          <FollowToggle key={group.id} source={group} type={FeedKey.Group}>
            {props => (
              <GroupCard
                group={group}
                isFollowing={props.isFollowing}
                onClick={() => handleFollowToggle(props)}
              />
            )}
          </FollowToggle>
        ))}
      </div>
    );
  }

  function renderUsers() {
    const mainClasses = classNames(classes.main, classes.mainRight);

    return (
      <div className={mainClasses}>
        <Typography paragraph variant="h4">
          {t('common:person_plural')}
        </Typography>
        {suggested_users.map(suggestedUser => (
          <FollowToggle key={suggestedUser.id} source={suggestedUser} type={FeedKey.User}>
            {props => (
              <UserCard
                user={suggestedUser}
                isFollowing={props.isFollowing}
                onClick={() => handleFollowToggle(props)}
              />
            )}
          </FollowToggle>
        ))}
      </div>
    );
  }

  function renderSidebar() {
    return (
      <Fragment>
        <ClientLogo client={client} />
        <Typography color="textSecondary" gutterBottom variant="h4">
          {t('onboarding:sidebarGroupsHeader')}
        </Typography>
        <Typography gutterBottom variant="subtitle2">
          {t('onboarding:sidebarGroupsSubHeader', { followCount: REQUIRED_FOLLOW_COUNT })}
        </Typography>
        <ActionContainer>{renderAction()}</ActionContainer>
      </Fragment>
    );
  }

  function renderMain() {
    return (
      <Grid container>
        <Grid item xs={12} sm={6}>
          {renderGroups()}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderUsers()}
        </Grid>
      </Grid>
    );
  }

  return <Layouts.Full2Column sticky offset={80} sidebar={renderSidebar()} main={renderMain()} />;
};

export default Follows;
